// Time Picker open/close/select/confirm. A bottom sheet below 768px; an
// inline card inside the cart drawer's pickup module from 768px (see the
// inline-expansion block below). Two DOM instances
// (layout/theme.liquid renders snippets/makc-time-picker.liquid once per
// pickup_type), distinguished by data-makc-time-picker-type -- see that
// snippet's own comment for why two static instances instead of one
// JS-toggled one.
//
// Same show/hide pattern as assets/makc-cart-shell.js and assets/
// makc-product-sheet.js -- a real hidden attribute toggle, paired with
// the shared window.makcScrollLock (ref-counted, so opening this from
// within the already-open, already-locked cart shell composes
// correctly with no special-casing needed -- see that file's own
// comment). Entrance/exit is a slide-up transform animation, same
// approach and 0.2s ease timing as the filter sheet's own panel
// (assets/makc-filter-sheet.css/.js) -- reused, not reinvented.
//
// The drag handle (.makc-time-picker__handle-row) now wires real
// pointer-drag-to-dismiss, matching the filter sheet's own handle
// exactly (assets/makc-filter-sheet.js) -- same Pointer Events API,
// same 0.25 dismiss-threshold ratio, same setPointerCapture/--dragging-
// class approach, wired identically per picker instance below.
//
// Real availability, replacing the old placeholder date-cycling / fixed
// 6-slot markup:
// - Standard: (later of shop "now" if today, or that day's opening
//   time) + 30min buffer, rounded up to the next 15-minute mark, is the
//   first slot; closing time minus 30min is the last; slots step every
//   15 minutes between the two. Schedulable up to the same 14-day
//   window assets/makc-trading-hours.js exports.
// - Made-to-order: a ONE-TIME GATE (computeMtoConstraint /
//   computeMtoDateForItem below) resolves the earliest valid pickup
//   DATE from the most restrictive of every made-to-order cart item's
//   own order_cutoff_time/lead_days/earliest_pickup_time metafields
//   (assets/makc-catalogue.js's getMtoConstraints()), clamped against
//   that day's real opening time and, if it lands on a Sunday, a fixed
//   3pm floor regardless of any other value. That date becomes both the
//   date-nav's starting position AND its left-chevron floor (never
//   reapplied per day past this point -- a day manually navigated to
//   AFTER it uses the exact same Standard-style floor logic against
//   that day's own hours, not MTO-specific closed-day handling).
// - Terminal states (No-Slots: a day with zero remaining slots;
//   Closed-Day: the trading-hours window marks that day closed;
//   Stale-Selection: a previously-confirmed cart selection that no
//   longer computes as valid on reopen) are all real, computed
//   detections now, not static placeholder markup -- see
//   snippets/makc-time-picker.liquid's own comment on how the DOM
//   supports toggling between all of them at runtime via `hidden`.
//
// Shop-timezone-safe "now" and the 14-day trading-hours window both
// come from window.makcTradingHours (assets/makc-trading-hours.js) --
// see that file's own comment for why a customer's browser clock/
// timezone can't be trusted here.
(function () {
  var pickers = Array.prototype.slice.call(document.querySelectorAll('[data-makc-time-picker]'));
  if (!pickers.length) return;

  var triggers = document.querySelectorAll('[data-makc-time-picker-trigger]');
  if (!triggers.length) return;

  var TRANSITION_MS = 200;
  var WINDOW_SIZE = 14; // matches snippets/makc-trading-hours-window.liquid's own 14-day export
  var DISMISS_THRESHOLD_RATIO = 0.25; // same value as assets/makc-filter-sheet.js's own drag-to-dismiss
  var STANDARD_BUFFER_MINUTES = 30;
  var SLOT_INTERVAL_MINUTES = 15;
  var SUNDAY_MTO_FLOOR_MINUTES = 15 * 60; // 3:00pm -- locked Sunday MTO clamp

  // Which property carries this sheet's open and close. Unlike the
  // filter sheet there is no 768+ modal to inherit a fade from -- at
  // that width this picker opens inline inside the cart row instead --
  // so reduced motion is the only case that fades, and it must, because
  // --makc-sheet-translate is 0px there (assets/makc-tokens.css): a
  // transform close would animate nothing, fire no transitionend, and
  // leave finish() to its timer. That is the iOS two-motion close this
  // branch fixes. Read per call so a mid-session change is handled, and
  // kept separate from the drag handling below, which is never damped
  // for reduced motion (CLAUDE.md "Motion").
  function usesFade() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Both animated properties are cleared together whichever one drove
  // the last open, so a change of motion preference between opens can
  // never leave the other one set -- same reasoning as the filter
  // sheet's own clearPanelAnimation().
  function clearPanelAnimation(panel) {
    if (!panel) return;
    panel.style.transform = '';
    panel.style.opacity = '';
  }

  var MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function pickerForType(type) {
    return pickers.filter(function (picker) {
      return picker.getAttribute('data-makc-time-picker-type') === type;
    })[0];
  }

  // ---- Inline expansion, 768px and up (Paper: Cart Drawer / Time Picker
  // Expanded / 768 and 1024 / Proposed) ---------------------------------
  //
  // Below 768 the picker is the bottom sheet it has always been, over the
  // full-screen cart. From 768 the cart is a side drawer, and a second
  // overlay on top of it would dim the page twice (two 0.45 backdrops
  // composite to ~0.70). So the same picker instance is moved into the
  // drawer's pickup module, directly under the row whose Edit time opened
  // it, and shown as an inline card: no backdrop, handle, header or close
  // X (Edit time becomes Cancel and is the dismissal), no z-index, no
  // slide animation, and NO scroll lock -- the drawer already holds one,
  // and an inline card that locked would have to unlock on every one of
  // the exit paths below, which is exactly the balance this avoids having
  // to maintain. Moving the one instance (rather than rendering a second)
  // keeps every binding, the __makcState stash, slot rendering and the
  // persist path shared with the sheet. The cart shell's pickup render
  // only toggles existing rows and labels and never rebuilds the module,
  // so a moved picker is never wiped by a cart re-render.
  //
  // Exit paths, each collapsing in place and restoring the picker to its
  // home in the layout: Cancel (the trigger again), Confirm, the drawer
  // closing (the cart shell calls collapseInline), the row disappearing
  // because its last item left the cart (same), the viewport crossing
  // 768 (the media-query listener below), and a bfcache restore (the
  // pageshow handler). None of them touch the scroll lock.
  var inlineLayoutQuery = window.matchMedia('(min-width: 768px)');

  pickers.forEach(function (picker) {
    picker.__makcHome = { parent: picker.parentNode, next: picker.nextSibling };
  });

  function isInline(picker) {
    return !!picker.__makcInlineTrigger;
  }

  // The row a trigger belongs to, and the module it sits in. Null when the
  // trigger is not inside the cart's pickup module, in which case the
  // sheet is used at every width.
  function inlineAnchorFor(trigger) {
    var row = trigger.closest('.makc-cart__pickup-row');
    if (!row || !row.parentNode || !row.parentNode.closest('.makc-cart__pickup-module')) return null;
    return row;
  }

  // The trigger's label is owned by the cart shell (Edit time / Select,
  // written on every render by setPickupRowState). While a picker is
  // expanded the label must read Cancel, and a render in that window -- a
  // reconcile landing, a stepper tap -- would otherwise put Edit time
  // back. The contract: the shell always records the resting label in
  // data-makc-pickup-resting-label and writes Cancel instead whenever
  // data-makc-pickup-expanded is present; this side only sets and clears
  // that attribute and restores the recorded resting label on collapse.
  // The same attribute is the cart's "picker open" Checkout blocker, so
  // the gate is re-run on every change of it.
  function markTriggerExpanded(trigger, expanded) {
    if (expanded) {
      trigger.setAttribute('data-makc-pickup-expanded', '');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.textContent = 'Cancel';
    } else {
      trigger.removeAttribute('data-makc-pickup-expanded');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.textContent = trigger.getAttribute('data-makc-pickup-resting-label') || 'Edit time';
    }
    if (window.makcCartShell && window.makcCartShell.refreshCheckoutGate) {
      window.makcCartShell.refreshCheckoutGate();
    }
  }

  // Brings the whole card into view inside the drawer's own scroll
  // region, the same region-scoped approach the cart uses for its
  // discount and note fields (not scrollIntoView, which can scroll the
  // page behind). If the card is taller than the region, its top wins.
  function revealInDrawer(picker) {
    var region = picker.closest('.makc-cart__scroll');
    if (!region) return;
    var regionRect = region.getBoundingClientRect();
    var cardRect = picker.getBoundingClientRect();
    var delta = 0;
    if (cardRect.bottom > regionRect.bottom) delta = cardRect.bottom - regionRect.bottom;
    if (cardRect.top - delta < regionRect.top) delta = cardRect.top - regionRect.top;
    if (!delta) return;
    try {
      region.scrollTo({ top: region.scrollTop + delta, behavior: 'smooth' });
    } catch (e) {
      region.scrollTop += delta;
    }
  }

  function collapseInlinePicker(picker) {
    if (!isInline(picker)) return;
    var trigger = picker.__makcInlineTrigger;
    picker.__makcInlineTrigger = null;
    picker.hidden = true;
    picker.classList.remove('makc-time-picker--inline');
    var panel = picker.querySelector('[data-makc-time-picker-panel]');
    if (panel) panel.setAttribute('aria-modal', 'true');
    var home = picker.__makcHome;
    if (home && home.parent) {
      home.parent.insertBefore(picker, home.next && home.next.parentNode === home.parent ? home.next : null);
    }
    markTriggerExpanded(trigger, false);
  }

  // Public: collapse one pickup type's inline picker, or all of them.
  // Safe to call at any time and any width -- a no-op when nothing is
  // expanded.
  function collapseInline(pickupType) {
    pickers.forEach(function (picker) {
      if (pickupType && picker.getAttribute('data-makc-time-picker-type') !== pickupType) return;
      collapseInlinePicker(picker);
    });
  }

  function attributeKeys(pickupType) {
    var isMto = pickupType === 'made_to_order';
    return {
      time: isMto ? 'Made-to-order pickup time' : 'Pickup time',
      date: isMto ? 'Made-to-order pickup date' : 'Pickup date',
      selectedAt: isMto ? 'Made-to-order pickup time selected at' : 'Pickup time selected at'
    };
  }

  // ---- Date-nav label formatting -------------------------------------
  // "Today, Tue 25 Aug" / "Tomorrow, Wed 26 Aug" / "Thu, 27 Aug" --
  // matches this component's own established compact format. "Today"
  // and "Tomorrow" are decided by comparing the entry's date string to
  // real shop-local today/tomorrow, never by position in the nav or the
  // window. Parses the entry's own 'YYYY-MM-DD' date string by hand
  // (split on '-') rather than through a real Date object, to avoid any
  // UTC/local reinterpretation risk entirely for a value that's pure
  // calendar data, not a moment in time.
  function formatDateNavLabel(dayEntry, shopNow) {
    var parts = dayEntry.date.split('-');
    var day = parseInt(parts[2], 10);
    var month = MONTH_ABBREVIATIONS[parseInt(parts[1], 10) - 1];
    var weekdayAbbrev = dayEntry.weekday.slice(0, 3);
    var relative = relativeDayWord(dayEntry, shopNow);
    if (relative === 'today') return 'Today, ' + weekdayAbbrev + ' ' + day + ' ' + month;
    if (relative === 'tomorrow') return 'Tomorrow, ' + weekdayAbbrev + ' ' + day + ' ' + month;
    return weekdayAbbrev + ', ' + day + ' ' + month;
  }

  function relativeDayWord(dayEntry, shopNow) {
    var todayStr = window.makcTradingHours.formatDateYMD(shopNow);
    var tomorrowStr = window.makcTradingHours.formatDateYMD(window.makcTradingHours.addDays(shopNow, 1));
    if (dayEntry.date === todayStr) return 'today';
    if (dayEntry.date === tomorrowStr) return 'tomorrow';
    return dayEntry.weekday.toLowerCase();
  }

  // ---- Core availability math -----------------------------------------

  function generateSlotsBetween(floorMinutes, ceilingMinutes) {
    var slots = [];
    for (var minutes = floorMinutes; minutes <= ceilingMinutes; minutes += SLOT_INTERVAL_MINUTES) {
      slots.push({ minutes: minutes, label: window.makcTradingHours.formatMinutesAsLabel(minutes) });
    }
    return slots;
  }

  // Locked formula: (later of now-if-today, opening time) + 30min
  // buffer, rounded up to the next 15-minute mark.
  function computeStandardFloor(dayEntry, shopNow, isToday) {
    var openMinutes = window.makcTradingHours.parseHHMM(dayEntry.opens);
    var base = openMinutes;
    if (isToday) {
      var nowMinutes = shopNow.getHours() * 60 + shopNow.getMinutes();
      base = Math.max(nowMinutes, openMinutes);
    }
    var withBuffer = base + STANDARD_BUFFER_MINUTES;
    return Math.ceil(withBuffer / SLOT_INTERVAL_MINUTES) * SLOT_INTERVAL_MINUTES;
  }

  // Locked formula: closing time minus 30 minutes.
  function computeCeiling(dayEntry) {
    return window.makcTradingHours.parseHHMM(dayEntry.closes) - STANDARD_BUFFER_MINUTES;
  }

  // One product's own earliest valid made-to-order pickup date + that
  // date's own floor (minutes since midnight). Re-anchors to the
  // current shop "now" on every call (no separate "after cutoff"
  // branch that hardcodes an extra day -- the date arithmetic below
  // naturally lands one day later on its own when isBeforeCutoff is
  // false, since dayOffset itself is what changes).
  function computeMtoDateForItem(constraints, shopNow) {
    var cutoffMinutes = window.makcTradingHours.parseHHMM(constraints.orderCutoffTime);
    // Cutoff is INCLUSIVE of the cutoff time itself -- now <= cutoff is
    // still "before cutoff". Seconds are folded in as a fraction of a
    // minute so an order at exactly cutoff (0 seconds past the minute)
    // reads as <=, not >.
    var nowMinutes = shopNow.getHours() * 60 + shopNow.getMinutes() + shopNow.getSeconds() / 60;
    var isBeforeCutoff = nowMinutes <= cutoffMinutes;
    var leadDays = parseInt(constraints.leadDays, 10) || 0;
    var dayOffset = isBeforeCutoff ? leadDays : leadDays + 1;

    var targetDate = window.makcTradingHours.addDays(shopNow, dayOffset);
    var targetDateStr = window.makcTradingHours.formatDateYMD(targetDate);
    var targetIsSunday = targetDate.getDay() === 0;

    var targetDayEntry = window.makcTradingHours.getDay(targetDateStr);
    var floorMinutes = window.makcTradingHours.parseHHMM(constraints.earliestPickupTime);

    if (targetDayEntry && !targetDayEntry.closed) {
      var openMinutes = window.makcTradingHours.parseHHMM(targetDayEntry.opens);
      if (openMinutes > floorMinutes) floorMinutes = openMinutes;
    }

    // Sunday MTO earliest is always >= 3pm regardless of any other
    // value -- a one-time-gate rule only, not reapplied to days
    // navigated to manually afterward (see this file's own top
    // comment).
    if (targetIsSunday && floorMinutes < SUNDAY_MTO_FLOOR_MINUTES) {
      floorMinutes = SUNDAY_MTO_FLOOR_MINUTES;
    }

    // None of earliest_pickup_time / opening time / the Sunday clamp
    // are guaranteed to already land on a real 15-minute slot mark, so
    // round up before ever comparing/returning this value.
    floorMinutes = Math.ceil(floorMinutes / SLOT_INTERVAL_MINUTES) * SLOT_INTERVAL_MINUTES;

    // Same-day guard: not part of the literal locked formula, but
    // necessary for correctness -- if lead_days/cutoff math lands the
    // target date on TODAY, the earliest pickup can never be earlier
    // than right now either, matching the same "past slots are hidden"
    // principle the Standard formula states explicitly. Applied last,
    // since it's the most restrictive possible additional floor and
    // only ever raises (never lowers) what's already been computed.
    var todayDateStr = window.makcTradingHours.formatDateYMD(shopNow);
    if (targetDateStr === todayDateStr) {
      var liveNowMinutes = shopNow.getHours() * 60 + shopNow.getMinutes();
      var liveFloor = Math.ceil((liveNowMinutes + STANDARD_BUFFER_MINUTES) / SLOT_INTERVAL_MINUTES) * SLOT_INTERVAL_MINUTES;
      if (liveFloor > floorMinutes) floorMinutes = liveFloor;
    }

    return { date: targetDateStr, floorMinutes: floorMinutes };
  }

  // Cart-wide: the single most restrictive (latest date, then latest
  // floor time on a tie) constraint across every made-to-order line in
  // the cart -- locked rule, mixed-MTO carts use the most restrictive
  // constraint, never a blended time.
  function computeMtoConstraint(cartItems, shopNow) {
    var results = [];
    cartItems.forEach(function (item) {
      var constraints = window.makcCatalogue.getMtoConstraints(item.product_id);
      if (constraints) {
        results.push(computeMtoDateForItem(constraints, shopNow));
      }
    });
    if (!results.length) return null;
    return results.reduce(function (latest, current) {
      if (current.date > latest.date) return current;
      if (current.date === latest.date && current.floorMinutes > latest.floorMinutes) return current;
      return latest;
    });
  }

  // This pickup type's own floor, as a DATE: today for Standard (always
  // its own earliest day); the made-to-order constraint's own computed
  // date for made_to_order, with the floor minutes that apply on that
  // date only. Dates, never window positions -- the 14-day window's
  // first entry is the day the HTML was rendered, which a cached page
  // can make yesterday (see assets/makc-trading-hours.js).
  function resolvePickerFloor(pickupType, shopNow) {
    var todayStr = window.makcTradingHours.formatDateYMD(shopNow);
    if (pickupType !== 'made_to_order') {
      return { floorDate: todayStr, floorMinutesOverride: null };
    }
    var cart = window.makcCartShell ? window.makcCartShell.getDisplayedCart() : null;
    var items = cart && cart.items ? cart.items : [];
    var constraint = computeMtoConstraint(items, shopNow);
    if (!constraint) {
      return { floorDate: todayStr, floorMinutesOverride: null };
    }
    return { floorDate: constraint.date, floorMinutesOverride: constraint.floorMinutes };
  }

  // Resolves one calendar date down to a terminal state (if any) and its
  // real slot list -- THE earliest-slot computation, shared by slot
  // generation (renderPickerAtIndex), picker-open validation and the
  // cart shell's own render-time check (cart open and the Checkout tap's
  // re-render, both via evaluateSelection). "Today" is decided by comparing the
  // date string against real shop-local today, never by position in
  // the window. The made-to-order constraint's own precomputed
  // floorMinutes is used ONLY on its own floor date -- every other day
  // uses the plain Standard-style floor against that day's own hours,
  // per the one-time-gate rule. A date the window doesn't cover
  // (possible only on a stale page) resolves to 'unknown_day': never
  // available, never mislabelled as closed.
  function resolveDayStateForDate(dateStr, floor, shopNow) {
    var dayEntry = dateStr ? window.makcTradingHours.getDay(dateStr) : null;
    if (!dayEntry) {
      return { state: 'unknown_day', slots: [], dayEntry: null };
    }
    if (dayEntry.closed) {
      return { state: 'closed_day', slots: [], dayEntry: dayEntry };
    }
    var todayStr = window.makcTradingHours.formatDateYMD(shopNow);
    var floorMinutes = dateStr === floor.floorDate && floor.floorMinutesOverride != null
      ? floor.floorMinutesOverride
      : computeStandardFloor(dayEntry, shopNow, dateStr === todayStr);
    var ceilingMinutes = computeCeiling(dayEntry);
    var slots = floorMinutes <= ceilingMinutes ? generateSlotsBetween(floorMinutes, ceilingMinutes) : [];
    if (!slots.length) {
      return { state: 'no_slots', slots: [], dayEntry: dayEntry };
    }
    return { state: 'available', slots: slots, dayEntry: dayEntry };
  }

  // ---- Date-nav index <-> calendar date -------------------------------
  // Index 0 is real shop-local today; index n is today + n days. Days
  // before today are never navigable. The rendered 14-day window is
  // only a lookup table for each date's hours (getDay), never the axis:
  // on a cached page its first entry can be yesterday and its last
  // entry can fall short of today + 13, in which case the nav simply
  // ends at the last date the window covers (lastNavigableIndex).
  function dateForIndex(index) {
    var shopNow = window.makcTradingHours.getShopNow();
    return window.makcTradingHours.formatDateYMD(window.makcTradingHours.addDays(shopNow, index));
  }

  function indexForDate(dateStr) {
    for (var i = 0; i < WINDOW_SIZE; i += 1) {
      if (dateForIndex(i) === dateStr) return i;
    }
    return -1;
  }

  function lastNavigableIndex() {
    var last = 0;
    for (var i = 0; i < WINDOW_SIZE; i += 1) {
      if (window.makcTradingHours.getDay(dateForIndex(i))) last = i;
    }
    return last;
  }

  // The floor's own nav index, clamped to the last navigable day if
  // the floor date is beyond it (a made-to-order constraint past the
  // window) rather than failing outright.
  function floorIndexFor(floor) {
    var index = indexForDate(floor.floorDate);
    var last = lastNavigableIndex();
    return index === -1 || index > last ? last : index;
  }

  // The one verdict on a stored pickup selection, used by picker open,
  // Checkout tap and cart open alike. Hard gate first: a stored date
  // before real shop-local today is stale no matter what the window
  // says (a cached page's window can still contain yesterday). Then the
  // made-to-order floor date, then the shared earliest-slot computation
  // for that exact date -- the stored label must be one of the slots
  // that computation would offer right now.
  //   status: 'none'  -- nothing stored for this pickup type
  //           'valid' -- stored date/time is still offerable; label set
  //           'stale' -- stored, but no longer offerable
  function evaluateSelection(pickupType) {
    var shopNow = window.makcTradingHours.getShopNow();
    var todayStr = window.makcTradingHours.formatDateYMD(shopNow);
    var floor = resolvePickerFloor(pickupType, shopNow);

    var persistedDate = getPersistedPickupDate(pickupType);
    var persistedTime = getPersistedPickupTime(pickupType);
    if (!persistedDate || !persistedTime) {
      return { status: 'none', date: null, label: null, floor: floor };
    }

    var result = { status: 'stale', date: persistedDate, label: null, floor: floor };
    if (persistedDate < todayStr) return result;
    if (persistedDate < floor.floorDate) return result;

    var dayState = resolveDayStateForDate(persistedDate, floor, shopNow);
    var persistedTimeLabel = extractPersistedTimeLabel(persistedTime);
    var stillOffered = dayState.state === 'available' && dayState.slots.some(function (slot) {
      return slot.label === persistedTimeLabel;
    });
    if (stillOffered) {
      result.status = 'valid';
      result.label = persistedTimeLabel;
    }
    return result;
  }

  // ---- Cart attribute read/write --------------------------------------

  function getPersistedPickupTime(pickupType) {
    var cart = window.makcCartShell ? window.makcCartShell.getDisplayedCart() : null;
    if (!cart || !cart.attributes) return null;
    return cart.attributes[attributeKeys(pickupType).time] || null;
  }

  function getPersistedPickupDate(pickupType) {
    var cart = window.makcCartShell ? window.makcCartShell.getDisplayedCart() : null;
    if (!cart || !cart.attributes) return null;
    return cart.attributes[attributeKeys(pickupType).date] || null;
  }

  function padTwoDigits(value) {
    return value < 10 ? '0' + value : String(value);
  }

  // Shop-time 'YYYY-MM-DD HH:MM', for the informational "selected at"
  // attribute (point 8) -- staff-facing only, judging orders placed
  // just past cutoff, never read back by this module itself.
  function formatShopTimestamp(shopNow) {
    var y = shopNow.getFullYear();
    var mo = padTwoDigits(shopNow.getMonth() + 1);
    var d = padTwoDigits(shopNow.getDate());
    var h = padTwoDigits(shopNow.getHours());
    var mi = padTwoDigits(shopNow.getMinutes());
    return y + '-' + mo + '-' + d + ' ' + h + ':' + mi;
  }

  // Merges into the cart's EXISTING attributes object rather than
  // replacing it wholesale, and always re-submits the current note
  // unchanged -- same "full-replace parameter, must preserve what's
  // already there" caution already established for assets/
  // makc-cart-shell.js's own reconcileLiveCart(). Writes three keys per
  // pickup type in one request: the human display value (unchanged
  // convention), the raw ISO date backing it (new -- lets a later reopen
  // or Checkout tap detect staleness across a real calendar-day
  // boundary, not just re-parse a relative "Today"/"Tomorrow" label
  // that would otherwise silently go stale at midnight), and the
  // shop-time "selected at" timestamp (point 8).
  function persistPickupTime(pickupType, displayText, isoDate) {
    var keys = attributeKeys(pickupType);
    var shopNow = window.makcTradingHours.getShopNow();

    return window.makcCart.getCart().then(function (currentCart) {
      var attributes = {};
      for (var key in currentCart.attributes) {
        if (Object.prototype.hasOwnProperty.call(currentCart.attributes, key)) {
          attributes[key] = currentCart.attributes[key];
        }
      }
      attributes[keys.time] = displayText;
      attributes[keys.date] = isoDate;
      attributes[keys.selectedAt] = formatShopTimestamp(shopNow);

      // Stamped at dispatch so the cart shell can order this response
      // against its own writes -- see cartWriteSequence in assets/
      // makc-cart-shell.js. The 0 fallback is dead code in practice:
      // the shell is always present (layout/theme.liquid loads it
      // before this script), and once any response has been applied a
      // 0 stamp would be dropped as superseded, not applied.
      var stamp = window.makcCartShell && window.makcCartShell.takeWriteStamp ? window.makcCartShell.takeWriteStamp() : 0;
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: currentCart.note, attributes: attributes })
      }).then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data, stamp: stamp };
        });
      });
    });
  }

  // Blanks this pickup type's own two attributes (display value + ISO
  // date) -- used by clearStaleSelection below when a stored selection
  // no longer computes as valid. Leaves the "selected at"
  // timestamp as-is; it's a historical record of when the now-stale
  // selection was originally made, not something that needs erasing.
  function clearPickupTime(pickupType) {
    var keys = attributeKeys(pickupType);
    return window.makcCart.getCart().then(function (currentCart) {
      var attributes = {};
      for (var key in currentCart.attributes) {
        if (Object.prototype.hasOwnProperty.call(currentCart.attributes, key)) {
          attributes[key] = currentCart.attributes[key];
        }
      }
      attributes[keys.time] = '';
      attributes[keys.date] = '';

      // Stamped at dispatch so the cart shell can order this response
      // against its own writes -- see cartWriteSequence in assets/
      // makc-cart-shell.js. The 0 fallback is dead code in practice:
      // the shell is always present (layout/theme.liquid loads it
      // before this script), and once any response has been applied a
      // 0 stamp would be dropped as superseded, not applied.
      var stamp = window.makcCartShell && window.makcCartShell.takeWriteStamp ? window.makcCartShell.takeWriteStamp() : 0;
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: currentCart.note, attributes: attributes })
      }).then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data, stamp: stamp };
        });
      });
    });
  }

  // ---- Rendering ---------------------------------------------------

  function updateLeadTimeNoteVisibility(picker, dayEntry, dayState, shopNow) {
    var wrapperEl = picker.querySelector('[data-makc-time-picker-lead-time-note]');
    var textEl = picker.querySelector('[data-makc-time-picker-lead-time-text]');
    if (!wrapperEl) return;

    // Hides the lead-time note unless Masa SPECIFICALLY is in the cart
    // -- standard made-to-order items (e.g. Akara) follow normal MTO
    // timing and need no explanatory note; Masa specifically has a
    // longer lead time, which is the actual reason this note exists.
    // Matches on item.handle (survives a title change, no catalogue
    // cross-reference needed -- already on every cart line item).
    // Intentional single-product exception to CLAUDE.md's data-driven
    // MTO timing model (order_cutoff_time/lead_days/earliest_pickup_time
    // metafields) -- confirmed with Kevin: fine as long as exactly one
    // product needs this note. If a second product ever needs the same
    // treatment, this should become metafield-driven instead of adding
    // a second hardcoded handle here.
    var cart = window.makcCartShell ? window.makcCartShell.getDisplayedCart() : null;
    var hasMasa = !!(cart && cart.items && cart.items.some(function (item) {
      return item.handle === 'masa';
    }));
    wrapperEl.hidden = !hasMasa;
    if (hasMasa && textEl && dayState.slots.length) {
      var earliestLabel = dayState.slots[0].label;
      var dayWord = relativeDayWord(dayEntry, shopNow);
      textEl.textContent = 'Masa needs a bit more time to prepare, which is why the earliest pickup is ' + earliestLabel + ' ' + dayWord + '.';
    }
  }

  function renderEmptyMessage(picker, variant) {
    var variants = Array.prototype.slice.call(picker.querySelectorAll('[data-makc-time-picker-empty-variant]'));
    variants.forEach(function (el) {
      el.hidden = el.getAttribute('data-makc-time-picker-empty-variant') !== variant;
    });
  }

  function bindSlotClickHandlers(picker) {
    var slots = Array.prototype.slice.call(picker.querySelectorAll('[data-makc-time-picker-slot]'));
    slots.forEach(function (slot) {
      slot.addEventListener('click', function () {
        slots.forEach(function (otherSlot) {
          otherSlot.classList.remove('makc-time-picker__slot--selected');
        });
        slot.classList.add('makc-time-picker__slot--selected');
        resetConfirmButton(picker);
      });
    });
  }

  // "1 PM" style -- uppercase, space before AM/PM, no minutes. A
  // deliberately different format from formatMinutesAsLabel's own
  // compact slot-button convention ("1pm", "1:30pm") -- that one lives
  // in assets/makc-trading-hours.js because trading-hours math needs it
  // too (display formatting for a value every consumer of that module
  // shares); this one is specific to the slot grid's own hour-group
  // headers, a presentation detail only this file's markup has any use
  // for, so it stays local rather than growing the shared module for a
  // single caller.
  function formatHourLabel(hour24) {
    var period = hour24 < 12 ? 'AM' : 'PM';
    var hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return hour12 + ' ' + period;
  }

  // Buckets an already-chronological slots array (generateSlotsBetween
  // steps forward in fixed 15-minute increments, never backward) into
  // per-hour groups without needing to re-sort -- a new group starts
  // exactly when the hour component of slot.minutes changes from the
  // previous slot's.
  function groupSlotsByHour(slots) {
    var groups = [];
    var currentHour = null;
    var currentGroup = null;
    slots.forEach(function (slot) {
      var hour = Math.floor(slot.minutes / 60);
      if (hour !== currentHour) {
        currentHour = hour;
        currentGroup = { hour: hour, slots: [] };
        groups.push(currentGroup);
      }
      currentGroup.slots.push(slot);
    });
    return groups;
  }

  // Slot buttons are rebuilt from scratch on every render (unlike the
  // old static 6-button markup) since the real slot count/labels vary
  // day to day -- "up to 18 possible slots/day, 6 visible before the
  // grid scrolls" per the locked formula, not a fixed count. Now
  // grouped by hour (Figma: Time Picker / Standard / Light, node
  // 1900:15153; Made-to-order / Light, node 1914:15204) rather than one
  // flat 2-column grid -- each hour renders its own label + nested
  // 2-column row, matching real slot volume (37-39 slots/day) instead
  // of the original 6-slot placeholder assumption.
  //
  // Only the dedicated hour-groups wrapper is cleared/rebuilt here, not
  // the outer [data-makc-time-picker-slot-grid] container itself --
  // that container is the 212px scroll box: renderPickerAtIndex() below
  // toggles its hidden attribute for No-Slots and the open path resets its
  // scrollTop, so it stays a stable element across renders rather than
  // being wiped and recreated with every slot rebuild.
  //
  // Each hour-slots row is NOT given a hardcoded height matching
  // Figma's own literal 96px (2 full rows) on this specific example
  // frame -- that value is only correct for a full, non-edge hour (4
  // slots). The first and last hour of a real day are routinely partial
  // (e.g. a floor of 11:00 exactly fills "11 AM", but a floor of 11:15
  // leaves only 3 slots in it, and the final hour before closing-minus-
  // 30 is just as often partial) -- forcing a fixed 2-row height on a
  // 1-or-3-slot group would leave visible dead space. Natural content
  // height reproduces Figma's own 96px automatically for every ordinary
  // full hour, and shrinks correctly for the real edge cases Figma's
  // one static example doesn't show.
  function renderSlots(picker, slots, selectedLabel) {
    var hourGroupsEl = picker.querySelector('[data-makc-time-picker-hour-groups]');
    if (!hourGroupsEl) return;
    hourGroupsEl.textContent = '';

    groupSlotsByHour(slots).forEach(function (group) {
      var groupEl = document.createElement('div');
      groupEl.className = 'makc-time-picker__hour-group';

      var labelEl = document.createElement('p');
      labelEl.className = 'makc-time-picker__hour-label';
      labelEl.textContent = formatHourLabel(group.hour);
      groupEl.appendChild(labelEl);

      var rowEl = document.createElement('div');
      rowEl.className = 'makc-time-picker__hour-slots';
      group.slots.forEach(function (slot) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'makc-time-picker__slot';
        button.setAttribute('data-makc-time-picker-slot', '');
        button.textContent = slot.label;
        if (selectedLabel && slot.label === selectedLabel) {
          button.classList.add('makc-time-picker__slot--selected');
        }
        rowEl.appendChild(button);
      });
      groupEl.appendChild(rowEl);

      hourGroupsEl.appendChild(groupEl);
    });

    bindSlotClickHandlers(picker);
    // A day change via the chevrons rebuilds the list while the picker
    // is already open -- each day starts at its own first slot.
    resetSlotGridScroll(picker);
  }

  // Renders one full date-nav index: date-nav text + chevron
  // disabled state, the terminal-state message OR the real slot grid,
  // notes visibility, and the stale-selection banner. Stashes the
  // resulting state on the picker element itself (picker.__makcState)
  // so the chevron handlers and Confirm's own handler can read back
  // "what index/state is currently showing" without recomputing it.
  function renderPickerAtIndex(picker, index, options) {
    options = options || {};
    var pickupType = picker.getAttribute('data-makc-time-picker-type');
    var shopNow = window.makcTradingHours.getShopNow();
    var floor = resolvePickerFloor(pickupType, shopNow);
    var dateStr = dateForIndex(index);
    var dayState = resolveDayStateForDate(dateStr, floor, shopNow);
    var dayEntry = dayState.dayEntry;

    picker.__makcState = { dateIndex: index, floor: floor, dayState: dayState };

    var dateEl = picker.querySelector('[data-makc-time-picker-date]');
    if (dateEl && dayEntry) dateEl.textContent = formatDateNavLabel(dayEntry, shopNow);

    var leftChevron = picker.querySelector('[data-makc-time-picker-date-nav="left"]');
    var rightChevron = picker.querySelector('[data-makc-time-picker-date-nav="right"]');
    var atFloor = !dateStr || dateStr <= floor.floorDate;
    var atEnd = index >= lastNavigableIndex();
    if (leftChevron) {
      leftChevron.disabled = atFloor;
      leftChevron.classList.toggle('makc-time-picker__date-nav-button--disabled', atFloor);
    }
    if (rightChevron) {
      rightChevron.disabled = atEnd;
      rightChevron.classList.toggle('makc-time-picker__date-nav-button--disabled', atEnd);
    }

    var emptyWrapper = picker.querySelector('[data-makc-time-picker-empty-message]');
    var slotGrid = picker.querySelector('[data-makc-time-picker-slot-grid]');
    var hourGroupsEl = picker.querySelector('[data-makc-time-picker-hour-groups]');
    var notesStack = picker.querySelector('[data-makc-time-picker-notes]');
    var staleWrapper = picker.querySelector('[data-makc-time-picker-stale-warning]');

    var showStale = !!options.stale;
    if (staleWrapper) staleWrapper.hidden = !showStale;

    if (dayState.state !== 'available') {
      if (emptyWrapper) emptyWrapper.hidden = false;
      // 'unknown_day' (a date the window doesn't cover) has no message
      // of its own -- the nav never offers such a day, so this branch
      // only guards against reaching one on a stale page.
      renderEmptyMessage(picker, dayState.state === 'no_slots' ? 'no_slots' : 'closed_day');
      // HOTFIX: the outer [data-makc-time-picker-slot-grid] container is
      // now hidden entirely for terminal states, not just cleared of its
      // inner hour-groups -- confirmed via live measurement that leaving
      // it visible (still reserving its own fixed 212px, as a sibling of
      // the now-visible empty-message) inflated the whole sheet well
      // beyond its normal available-state height instead of shrinking
      // it, producing a large dead-space gap. Figma's own Closed-Day/
      // No-Slots frames (nodes 1926:15291, 1923:15236) have no slot-grid
      // element in them at all and are deliberately shorter overall
      // (324.5px) than the available-state frame (433px) -- hiding this
      // element lets the sheet naturally shrink to match that, rather
      // than trying to preserve the available-state height artificially.
      // The hour-groups wrapper is still cleared too (not just hidden
      // alongside its parent) so it starts empty the next time this
      // picker returns to an available state.
      if (slotGrid) slotGrid.hidden = true;
      if (hourGroupsEl) hourGroupsEl.textContent = '';
      if (notesStack) notesStack.hidden = true;
    } else {
      if (emptyWrapper) emptyWrapper.hidden = true;
      if (slotGrid) slotGrid.hidden = false;
      renderSlots(picker, dayState.slots, options.selectedLabel || null);
      if (notesStack) {
        // Stale-Selection drops the notes section entirely, matching
        // this component's own original design (nothing is selected,
        // so a freshness/lead-time note about a specific chosen time
        // doesn't apply yet).
        notesStack.hidden = showStale;
        if (!showStale && pickupType === 'made_to_order') {
          updateLeadTimeNoteVisibility(picker, dayEntry, dayState, shopNow);
        }
      }
    }

    resetConfirmButton(picker);
  }

  // Determines a persisted selection's own display TIME by taking the
  // LAST comma segment, not index [1] -- the date-nav label itself can
  // now contain more than one comma ("Today, Tue 25 Aug, 1pm"), unlike
  // the old two-segment "Today, 1pm" format, so index [1] would grab
  // part of the date instead of the time.
  function extractPersistedTimeLabel(persistedTime) {
    var segments = persistedTime.split(',');
    return segments[segments.length - 1].trim();
  }

  // Real starting state for a fresh open: restores a still-valid
  // persisted selection exactly where it was, detects and flags a
  // no-longer-valid one as Stale-Selection (opening on the stored day
  // if it's still navigable, else on the floor), or (nothing persisted
  // yet) starts at this picker's own natural floor. The verdict itself
  // is evaluateSelection's -- the same one Checkout tap and cart open
  // use.
  function determineOpenState(picker) {
    var pickupType = picker.getAttribute('data-makc-time-picker-type');
    var evaluation = evaluateSelection(pickupType);
    var floorIndex = floorIndexFor(evaluation.floor);

    var floorDate = evaluation.floor.floorDate;
    if (evaluation.status === 'none') {
      return { index: floorIndex, stale: false, selectedLabel: null, floorDate: floorDate };
    }
    if (evaluation.status === 'valid') {
      var validIndex = indexForDate(evaluation.date);
      return { index: validIndex >= 0 ? validIndex : floorIndex, stale: false, selectedLabel: evaluation.label, floorDate: floorDate };
    }

    var staleIndex = evaluation.date >= floorDate ? indexForDate(evaluation.date) : -1;
    return { index: staleIndex >= 0 ? staleIndex : floorIndex, stale: true, selectedLabel: null, floorDate: floorDate };
  }

  // No slot selected is a real, handled case (disabled + plain
  // "Confirm"), matching this project's established "real disabled
  // attribute plus its own visual class, native :disabled alone isn't
  // relied on" convention.
  function resetConfirmButton(picker) {
    var confirmButton = picker.querySelector('[data-makc-time-picker-confirm]');
    if (!confirmButton) return;
    var selectedSlot = picker.querySelector('[data-makc-time-picker-slot].makc-time-picker__slot--selected');
    if (!selectedSlot) {
      confirmButton.disabled = true;
      confirmButton.classList.add('makc-time-picker__confirm--disabled');
      confirmButton.textContent = 'Confirm';
      return;
    }
    confirmButton.disabled = false;
    confirmButton.classList.remove('makc-time-picker__confirm--disabled');
    confirmButton.textContent = 'Confirm · ' + selectedSlot.textContent.trim();
  }

  // The slot grid keeps its scroll offset across close/reopen: its hour
  // groups are cleared and rebuilt within one task, so the offset from
  // the last open is never clamped, and hiding the picker root doesn't
  // reset it either. Left alone, the picker reopened mid-list with the
  // earliest slots above the fold. Reset once the root is displayed
  // again (scrollTop on a display:none subtree is a no-op) and before
  // the slide-up starts.
  function resetSlotGridScroll(picker) {
    var slotGrid = picker.querySelector('[data-makc-time-picker-slot-grid]');
    if (slotGrid) slotGrid.scrollTop = 0;
  }

  function playOpenAnimation(picker) {
    // Cleared here as well as in finish(): reopening while a close is
    // still running would otherwise leave the backdrop mid-fade-out.
    picker.classList.remove('makc-time-picker--closing');
    picker.hidden = false;
    window.makcScrollLock.lock();
    resetSlotGridScroll(picker);

    var panel = picker.querySelector('[data-makc-time-picker-panel]');
    if (!panel) return;
    // Toggling hidden alone can't animate a display:none element -- the
    // "start" position (off-screen, no transition) has to be set
    // synchronously first, then the transition restored and the target
    // position set on the next frame so the browser actually animates
    // the change instead of coalescing it. Same technique as the filter
    // sheet's own openSheet() (assets/makc-filter-sheet.js).
    var fade = usesFade();
    panel.classList.add('makc-time-picker__panel--instant');
    clearPanelAnimation(panel);
    if (fade) {
      panel.style.opacity = '0';
    } else {
      panel.style.transform = 'translateY(var(--makc-sheet-translate))';
    }
    void panel.offsetHeight; // force reflow before restoring the transition
    panel.classList.remove('makc-time-picker__panel--instant');
    requestAnimationFrame(function () {
      if (fade) {
        panel.style.opacity = '1';
      } else {
        panel.style.transform = 'translateY(0)';
      }
    });
  }

  // One info line per open -- pickup type, the shop time the floor was
  // computed from and which clock produced it, the floor date and the
  // index actually opened at -- so a wrong-floor report can be read
  // straight from the console instead of reconstructed (the 2026-09-15
  // 11am-at-3:50pm sighting had nothing to go on).
  function renderForOpen(picker) {
    var openState = determineOpenState(picker);
    var shopNow = window.makcTradingHours.getShopNow();
    console.info('[makc-time-picker] open', {
      pickupType: picker.getAttribute('data-makc-time-picker-type'),
      shopNow: window.makcTradingHours.formatDateYMD(shopNow) + ' ' + padTwoDigits(shopNow.getHours()) + ':' + padTwoDigits(shopNow.getMinutes()),
      clockSource: window.makcTradingHours.getClockSource ? window.makcTradingHours.getClockSource() : 'unknown',
      floorDate: openState.floorDate,
      openedIndex: openState.index,
      stale: openState.stale
    });
    renderPickerAtIndex(picker, openState.index, { stale: openState.stale, selectedLabel: openState.selectedLabel });
  }

  function openSheet(picker) {
    renderForOpen(picker);
    playOpenAnimation(picker);
  }

  function openInline(picker, trigger, anchorRow) {
    // One row open at a time, matching the sheet's one-picker behaviour.
    pickers.forEach(function (other) {
      if (other !== picker) collapseInlinePicker(other);
    });
    renderForOpen(picker);
    anchorRow.parentNode.insertBefore(picker, anchorRow.nextSibling);
    picker.classList.add('makc-time-picker--inline');
    var panel = picker.querySelector('[data-makc-time-picker-panel]');
    // Inline it is part of the drawer, not a modal dialog over it.
    if (panel) panel.removeAttribute('aria-modal');
    picker.__makcInlineTrigger = trigger;
    picker.hidden = false;
    resetSlotGridScroll(picker);
    markTriggerExpanded(trigger, true);
    revealInDrawer(picker);
  }

  // A bfcache restore brings the page back exactly as it was left --
  // possibly with a picker open and a __makcState rendered against a
  // clock reading from before the tab was left. Same reset the cart
  // shell does on this event (assets/makc-cart-shell.js): close
  // immediately (no exit animation), release the scroll lock that
  // the sheet took, and drop the stash so the next open recomputes
  // everything from the current clock.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    pickers.forEach(function (picker) {
      // An inline picker never took the scroll lock, so it must not
      // release one -- collapse it and leave the lock to the drawer
      // (the cart shell's own pageshow reset closes that).
      if (isInline(picker)) {
        collapseInlinePicker(picker);
      } else if (!picker.hidden) {
        var panel = picker.querySelector('[data-makc-time-picker-panel]');
        picker.hidden = true;
        picker.classList.remove('makc-time-picker--closing');
        clearPanelAnimation(panel);
        window.makcScrollLock.unlock();
      }
      delete picker.__makcState;
    });
  });

  function closePicker(picker) {
    if (isInline(picker)) {
      collapseInlinePicker(picker);
      return;
    }
    var panel = picker.querySelector('[data-makc-time-picker-panel]');
    if (!panel) {
      picker.hidden = true;
      window.makcScrollLock.unlock();
      return;
    }

    // Fades the backdrop out alongside the panel's slide (assets/
    // makc-time-picker.css); the root's [hidden] is only set in
    // finish(), far too late to fade anything.
    picker.classList.add('makc-time-picker--closing');
    // Signals the exit NOW, not at finish() -- identical to the filter
    // sheet's own closeSheet(), and the two must not drift. See
    // assets/makc-scroll-lock.js for why the cart pill's entrance was
    // queueing behind the slide. Scroll stays locked; unlock() and the
    // position restore below are untouched.
    if (window.makcScrollLock.beginClose) window.makcScrollLock.beginClose();
    if (usesFade()) {
      panel.style.opacity = '0';
    } else {
      panel.style.transform = 'translateY(var(--makc-sheet-translate))';
    }
    var done = false;
    // transitionend bubbles, so a transition ending on any control INSIDE
    // the panel (a slot's colour swap, Confirm's or a chevron's press
    // scale) would otherwise finish the close early and cut the slide
    // short. Only the panel's own transition counts. The setTimeout
    // fallback below calls finish() with no event, which still passes.
    function finish(event) {
      if (event && event.target !== panel) return;
      if (done) return;
      done = true;
      panel.removeEventListener('transitionend', finish);
      picker.hidden = true;
      picker.classList.remove('makc-time-picker--closing');
      clearPanelAnimation(panel);
      window.makcScrollLock.unlock();
    }
    panel.addEventListener('transitionend', finish);
    setTimeout(finish, TRANSITION_MS + 50);
  }

  triggers.forEach(function (trigger) {
    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('click', function () {
      var picker = pickerForType(trigger.getAttribute('data-makc-time-picker-trigger'));
      if (!picker) return;
      // Cancel: the same trigger, while its picker is expanded inline.
      if (isInline(picker)) {
        collapseInlinePicker(picker);
        return;
      }
      var anchorRow = inlineLayoutQuery.matches ? inlineAnchorFor(trigger) : null;
      if (anchorRow) {
        openInline(picker, trigger, anchorRow);
      } else {
        openSheet(picker);
      }
    });
  });

  // Crossing 768 in either direction: whatever is open was built for the
  // other layout, so close it -- an inline card collapses (no lock to
  // release), a sheet closes through closePicker (which releases the one
  // it took). The customer reopens in the right form for the new width.
  function handleLayoutChange() {
    pickers.forEach(function (picker) {
      if (isInline(picker)) {
        collapseInlinePicker(picker);
      } else if (!picker.hidden) {
        closePicker(picker);
      }
    });
  }
  if (inlineLayoutQuery.addEventListener) {
    inlineLayoutQuery.addEventListener('change', handleLayoutChange);
  } else if (inlineLayoutQuery.addListener) {
    inlineLayoutQuery.addListener(handleLayoutChange);
  }

  // The cart shell calls this from renderPickupModule -- on cart open,
  // on every render, and on the Checkout tap's own re-render -- when
  // evaluateSelection says a stored selection is stale: the attribute
  // is cleared (a real cart write, hence async) and the cleared cart
  // comes back through refreshPickupModule to re-render the row
  // unresolved and recompute the checkout gate. There is no reprompt:
  // the picker is never opened from here (an earlier Checkout-tap path
  // that dropped it open in its Stale-Selection state was replaced by
  // the cart shell's persistent gate message). Guarded per pickup type
  // so the renders that happen while the clear is in flight don't fire
  // it again.
  var clearInFlight = {};

  function clearStaleSelection(pickupType) {
    if (clearInFlight[pickupType]) return;
    clearInFlight[pickupType] = true;
    clearPickupTime(pickupType).then(function (result) {
      clearInFlight[pickupType] = false;
      if (!result.ok) {
        console.error('[makc-time-picker] clearing a stale ' + pickupType + ' pickup selection failed', result.data);
        return;
      }
      if (window.makcCartShell && window.makcCartShell.refreshPickupModule) {
        window.makcCartShell.refreshPickupModule(result.data, result.stamp);
      }
    }).catch(function (error) {
      clearInFlight[pickupType] = false;
      console.error('[makc-time-picker] clearing a stale ' + pickupType + ' pickup selection failed', error);
    });
  }

  // attributeKeys is exposed so the cart shell blanks exactly these keys
  // (time + date, never "selected at") when the last line of a pickup
  // type leaves the cart -- see assets/makc-cart-shell.js's pickup-reset
  // block -- rather than re-hardcoding the attribute names there.
  window.makcTimePicker = {
    attributeKeys: attributeKeys,
    evaluateSelection: evaluateSelection,
    clearStaleSelection: clearStaleSelection,
    collapseInline: collapseInline
  };

  pickers.forEach(function (picker) {
    var pickupType = picker.getAttribute('data-makc-time-picker-type');
    var panel = picker.querySelector('[data-makc-time-picker-panel]');
    var closeButton = picker.querySelector('[data-makc-time-picker-close]');
    var backdrop = picker.querySelector('[data-makc-time-picker-backdrop]');
    var handleRow = picker.querySelector('[data-makc-time-picker-handle]');
    var confirmButton = picker.querySelector('[data-makc-time-picker-confirm]');
    var errorEl = picker.querySelector('[data-makc-time-picker-error]');
    var dateEl = picker.querySelector('[data-makc-time-picker-date]');
    var leftChevron = picker.querySelector('[data-makc-time-picker-date-nav="left"]');
    var rightChevron = picker.querySelector('[data-makc-time-picker-date-nav="right"]');

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        closePicker(picker);
      });
    }

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closePicker(picker);
      });
    }

    // Drag-to-dismiss -- identical mechanism to assets/makc-filter-
    // sheet.js's own handle (see that file's own comment for the full
    // reasoning: Pointer Events unify touch/mouse, setPointerCapture
    // keeps tracking the same pointer even if it leaves the handle's own
    // bounds mid-drag, --dragging suppresses the transition so the panel
    // follows the finger 1:1 instead of easing toward each frame, and
    // the 0.25 threshold decides dismiss vs. snap-back on release).
    if (panel && handleRow && window.PointerEvent) {
      var dragging = false;
      var dragStartY = 0;
      var dragStartHeight = 0;

      handleRow.addEventListener('pointerdown', function (event) {
        dragging = true;
        dragStartY = event.clientY;
        dragStartHeight = panel.getBoundingClientRect().height;
        panel.classList.add('makc-time-picker__panel--dragging');
        handleRow.setPointerCapture(event.pointerId);
      });

      handleRow.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        var deltaY = Math.max(0, event.clientY - dragStartY);
        panel.style.transform = 'translateY(' + deltaY + 'px)';
      });

      var endDrag = function (event) {
        if (!dragging) return;
        dragging = false;
        panel.classList.remove('makc-time-picker__panel--dragging');
        var deltaY = Math.max(0, event.clientY - dragStartY);
        if (deltaY > dragStartHeight * DISMISS_THRESHOLD_RATIO) {
          closePicker(picker);
        } else {
          panel.style.transform = 'translateY(0)';
        }
      };

      handleRow.addEventListener('pointerup', endDrag);
      handleRow.addEventListener('pointercancel', endDrag);
    }

    if (leftChevron) {
      leftChevron.addEventListener('click', function () {
        if (leftChevron.disabled || !picker.__makcState) return;
        renderPickerAtIndex(picker, picker.__makcState.dateIndex - 1, { stale: false, selectedLabel: null });
      });
    }

    if (rightChevron) {
      rightChevron.addEventListener('click', function () {
        if (rightChevron.disabled || !picker.__makcState) return;
        renderPickerAtIndex(picker, picker.__makcState.dateIndex + 1, { stale: false, selectedLabel: null });
      });
    }

    if (!confirmButton) return;

    confirmButton.addEventListener('click', function () {
      if (confirmButton.disabled) return;

      var selectedSlot = picker.querySelector('[data-makc-time-picker-slot].makc-time-picker__slot--selected');
      if (!selectedSlot || !picker.__makcState) return;

      if (errorEl) errorEl.hidden = true;

      var dayEntry = picker.__makcState.dayState.dayEntry;
      var dayLabel = dateEl ? dateEl.textContent.trim() : '';
      var displayText = dayLabel + ', ' + selectedSlot.textContent.trim();
      var isoDate = dayEntry ? dayEntry.date : '';

      // Loading-state treatment matches assets/makc-product-sheet.js's
      // own "Add to order" button exactly (disable + transient label,
      // restore both on failure) -- there's a real network delay here
      // between tapping Confirm and the time actually being persisted.
      var normalButtonText = confirmButton.textContent;
      confirmButton.disabled = true;
      confirmButton.textContent = 'Confirming…';

      persistPickupTime(pickupType, displayText, isoDate).then(function (result) {
        confirmButton.disabled = false;

        if (!result.ok) {
          confirmButton.textContent = normalButtonText;
          if (errorEl) {
            errorEl.textContent = 'Something went wrong — please try again.';
            errorEl.hidden = false;
          }
          return;
        }

        // result.data is the fresh, full cart /cart/update.js already
        // returned (already reflecting the attributes just persisted),
        // so handing it to refreshPickupModule() re-decides this row's
        // (and the other row's, since relevance can change too)
        // resolved state and Checkout eligibility from real server
        // truth, not a client-reconstructed guess.
        if (window.makcCartShell && window.makcCartShell.refreshPickupModule) {
          window.makcCartShell.refreshPickupModule(result.data, result.stamp);
        }
        closePicker(picker);
      }).catch(function () {
        confirmButton.disabled = false;
        confirmButton.textContent = normalButtonText;
        if (errorEl) {
          errorEl.textContent = 'Something went wrong — please try again.';
          errorEl.hidden = false;
        }
      });
    });
  });
})();
